import { createContext, useState, ReactNode } from "react";

interface QuizAnswerState {
  [quizId: string]: number; // correctIndex selected
}

interface CommentState {
  [groupId: string]: string;
}

export interface UserInputContextType {
  // Quiz answers
  quizAnswers: QuizAnswerState;
  setQuizAnswer: (quizId: string, answerIndex: number) => void;
  getQuizAnswer: (quizId: string) => number | undefined;
  isQuizCompleted: (quizId: string) => boolean;
  clearAllQuizAnswers: () => void;

  // Comments
  comments: CommentState;
  setComment: (groupId: string, text: string) => void;
  getComment: (groupId: string) => string;
  clearAllComments: () => void;

  // Tooltip tracking - only show on first bookmark button
  hasSeenBookmarkTooltip: boolean;
  setHasSeenBookmarkTooltip: () => void;
}

const UserInputContext = createContext<UserInputContextType | undefined>(
  undefined,
);

export { UserInputContext };

export function UserInputProvider({ children }: { children: ReactNode }) {
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswerState>({});
  const [comments, setComments] = useState<CommentState>({});
  const [hasSeenBookmarkTooltip, setHasSeenTooltip] = useState(false);

  // Quiz methods
  const setQuizAnswer = (quizId: string, answerIndex: number) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [quizId]: answerIndex,
    }));
  };

  const getQuizAnswer = (quizId: string): number | undefined => {
    return quizAnswers[quizId];
  };

  const isQuizCompleted = (quizId: string): boolean => {
    return quizAnswers[quizId] !== undefined;
  };

  const clearAllQuizAnswers = () => {
    setQuizAnswers({});
  };

  // Comment methods
  const setComment = (groupId: string, text: string) => {
    setComments((prev) => ({
      ...prev,
      [groupId]: text,
    }));
  };

  const getComment = (groupId: string): string => {
    return comments[groupId] || "";
  };

  const clearAllComments = () => {
    setComments({});
  };

  // Tooltip tracking
  const setHasSeenBookmarkTooltip = () => {
    setHasSeenTooltip(true);
  };

  return (
    <UserInputContext.Provider
      value={{
        quizAnswers,
        setQuizAnswer,
        getQuizAnswer,
        isQuizCompleted,
        clearAllQuizAnswers,
        comments,
        setComment,
        getComment,
        clearAllComments,
        hasSeenBookmarkTooltip,
        setHasSeenBookmarkTooltip,
      }}
    >
      {children}
    </UserInputContext.Provider>
  );
}
